import { Command, Shift, ShiftsGroupEvent, UserRole } from '@nextforce/api'
import { makeStyles } from '@rneui/themed'
import { groupBy } from 'lodash'
import React, { memo, useCallback, useMemo } from 'react'
import { View } from 'react-native'
import { ShiftView } from './shift'
import { OpenShiftCard } from './shifts/open_shift_schedule'
import { ShiftsGroupEventSummaryView } from './summary'

type Props = {
  role: UserRole
  event: ShiftsGroupEvent
  expanded: boolean
  onShiftDetails(shift: Shift): void
  onEventExpand(): void
  onCommandButtonPress(command: Command): Promise<void>
  showSummaryTiles: boolean
}

export const isShiftInSummary = (shift: Shift) => {
  return !(shift.critical || shift.note || shift.open)
}

export const eventHasSummary = (event: ShiftsGroupEvent): boolean => {
  return (
    event.calendar !== 'helper' &&
    event.shifts.filter(isShiftInSummary).length > 1
  )
}

export interface ShiftGroups {
  priority: Shift[]
  open: Shift[]
  normal: Shift[]
}

// Memoized child components
const MemoizedShiftView = memo(ShiftView)
const MemoizedShiftsGroupEventSummaryView = memo(ShiftsGroupEventSummaryView)
const MemoizedOpenShiftCard = memo(OpenShiftCard)

export const eventShiftGroups = (event: ShiftsGroupEvent): ShiftGroups => {
  const groups = groupBy(event.shifts, shift => {
    if (shift.note || shift.critical) {
      return 'priority'
    }

    if (shift.open) {
      return 'open'
    }

    return 'normal'
  })

  return {
    priority: groups.priority ?? [],
    open: groups.open ?? [],
    normal: groups.normal ?? [],
  }
}

export const ShiftsGroupEventView = memo(function ShiftsGroupEventView({
  role,
  event,
  expanded,
  onShiftDetails,
  onEventExpand,
  showSummaryTiles,
  onCommandButtonPress,
}: Props) {
  const styles = useStyles()

  const hasSummary = useMemo(
    () => eventHasSummary(event) && showSummaryTiles,
    [event, showSummaryTiles]
  )

  const shiftGroups = useMemo(() => eventShiftGroups(event), [event])

  const summaryShifts = useMemo(
    () => event.shifts.filter(isShiftInSummary),
    [event.shifts]
  )

  const summaryEvent = useMemo(
    () => ({ ...event, shifts: summaryShifts }),
    [event, summaryShifts]
  )

  const handleShiftDetails = useCallback(
    (shift: Shift) => {
      onShiftDetails(shift)
    },
    [onShiftDetails]
  )

  const handleCommandButtonPress = useCallback(
    (command: Command) => {
      return onCommandButtonPress(command)
    },
    [onCommandButtonPress]
  )

  const handleExpand = useCallback(() => {
    onEventExpand()
  }, [onEventExpand])

  const renderShiftView = useCallback(
    (shift: Shift) => (
      <MemoizedShiftView
        key={shift.id}
        role={role}
        calendar={event.calendar}
        event={event}
        hasSummary={hasSummary}
        shift={shift}
        onShiftDetails={handleShiftDetails}
        onCommandButtonPress={handleCommandButtonPress}
      />
    ),
    [role, event, hasSummary, handleShiftDetails, handleCommandButtonPress]
  )

  const priorityShiftViews = useMemo(
    () => shiftGroups.priority.map(renderShiftView),
    [shiftGroups.priority, renderShiftView]
  )

  const openShiftViews = useMemo(
    () => shiftGroups.open.map(renderShiftView),
    [shiftGroups.open, renderShiftView]
  )

  const normalShiftViews = useMemo(
    () => shiftGroups.normal.map(renderShiftView),
    [shiftGroups.normal, renderShiftView]
  )

  return (
    <View>
      {priorityShiftViews}
      {openShiftViews}
      {hasSummary ? (
        <MemoizedShiftsGroupEventSummaryView
          event={summaryEvent}
          onEventExpand={handleExpand}
          expanded={expanded}
          groupsCount={shiftGroups.normal.length}
        />
      ) : null}
      <View>
        {hasSummary
          ? expanded && (
              <View style={styles.summaryShiftsContainer}>
                <View style={styles.expandedShiftsLeftBorder}></View>
                <View style={styles.expandedShiftsContainer}>
                  {normalShiftViews}
                </View>
              </View>
            )
          : normalShiftViews}
      </View>
    </View>
  )
})

type OpenShiftsGroupEventViewProps = {
  event: ShiftsGroupEvent
  performOpenShiftAction(command: Command): Promise<Shift | undefined>
}

export const OpenShiftsGroupEventView = memo(function OpenShiftsGroupEventView({
  event,
  performOpenShiftAction,
}: OpenShiftsGroupEventViewProps) {
  return (
    <View>
      {event.shifts.map(shift => (
        <MemoizedOpenShiftCard
          key={shift.id}
          shift={shift}
          performOpenShiftAction={performOpenShiftAction}
        />
      ))}
    </View>
  )
})

const useStyles = makeStyles(theme => ({
  summaryShiftsContainer: { flexDirection: 'row' },
  expandedShiftsContainer: {
    flex: 1,
    paddingLeft: 10,
  },
  expandedShiftsLeftBorder: {
    backgroundColor: theme.colors?.grey7,
    width: 5,
    borderRadius: 10,
  },
  expandedContainer: {
    flex: 1,
    paddingLeft: 10,
    borderLeftWidth: 5,
    borderLeftColor: theme.colors?.grey7,
    borderRadius: 10,
    marginVertical: 5,
  },
}))
